/**
 * Admin Setup → Tracking PRO tab.
 *
 * Configures live courier tracking for ASNs. The provider API key and webhook
 * secret are environment config, not settings — this tab shows whether they
 * are present and controls only behaviour: the master switch, whether courier
 * status may move the ASN itself, and the carrier slug overrides.
 *
 * Toggles patch PlatformSettings inline (no Save button), matching the OTD and
 * Quality tabs.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Loader2, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

type TrackingFlag = 'tracking_pro_enabled' | 'tracking_pro_update_asn_status';

interface TrackingState {
  tracking_pro_enabled: boolean;
  tracking_pro_update_asn_status: boolean;
}

const INITIAL: TrackingState = {
  tracking_pro_enabled: false,
  tracking_pro_update_asn_status: false,
};

const ROW_META: Array<{ key: TrackingFlag; title: string; description: string }> = [
  {
    key: 'tracking_pro_enabled',
    title: 'Enable live courier tracking',
    description:
      'Registers dispatched ASNs with the courier tracking provider and shows real '
      + 'courier status on the ASN list and detail. Requires the provider API key to '
      + 'be configured in the environment — without it this switch has no effect.',
  },
  {
    key: 'tracking_pro_update_asn_status',
    title: 'Let courier status update the ASN',
    description:
      'When ON, courier scans advance the ASN itself (submitted → in transit → '
      + 'delivered) and fill the actual delivery date. Forward-only: an ASN already '
      + 'received or cancelled is never rewritten. When OFF, courier status is shown '
      + 'alongside the ASN but changes nothing.',
  },
];

export function TrackingProTab() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<TrackingState>(INITIAL);
  const [pendingKey, setPendingKey] = useState<TrackingFlag | null>(null);
  const [configured, setConfigured] = useState(false);
  // Carrier slug overrides. `slugDefaults` seeds the placeholders so an empty
  // input reads as "using the built-in default", not "no carrier".
  const [slugDefaults, setSlugDefaults] = useState<Record<string, string>>({});
  const [slugs, setSlugs] = useState<Record<string, string>>({});
  const [slugsSaved, setSlugsSaved] = useState<Record<string, string>>({});
  const [slugsSaving, setSlugsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await platformSettingsAPI.getSettings();
        if (cancelled) return;
        const d = res.data;
        setState({
          tracking_pro_enabled: d.tracking_pro_enabled ?? false,
          tracking_pro_update_asn_status: d.tracking_pro_update_asn_status ?? false,
        });
        setConfigured(d.tracking_pro_configured ?? false);
        const defaults = d.tracking_pro_courier_slug_defaults ?? {};
        setSlugDefaults(defaults);
        const overrides = d.tracking_pro_courier_slugs ?? {};
        setSlugs({ ...overrides });
        setSlugsSaved({ ...overrides });
      } catch (err: unknown) {
        console.error('Failed to load Tracking PRO settings', err);
        toast.error('Failed to load Tracking PRO settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (key: TrackingFlag, next: boolean) => {
    const prev = state[key];
    if (prev === next) return;
    setState((s) => ({ ...s, [key]: next }));
    setPendingKey(key);
    try {
      await platformSettingsAPI.patchSettings({ [key]: next });
      toast.success('Tracking PRO setting updated');
    } catch (err: unknown) {
      console.error('Failed to save Tracking PRO setting', err);
      toast.error('Failed to save Tracking PRO setting');
      setState((s) => ({ ...s, [key]: prev }));
    } finally {
      setPendingKey(null);
    }
  };

  const slugsDirty = JSON.stringify(slugs) !== JSON.stringify(slugsSaved);

  const saveSlugs = async () => {
    // Drop blanks so an emptied field falls back to the built-in default
    // rather than persisting "auto-detect" by accident.
    const cleaned: Record<string, string> = {};
    Object.entries(slugs).forEach(([courier, slug]) => {
      const trimmed = slug.trim();
      if (trimmed) cleaned[courier] = trimmed;
    });
    setSlugsSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ tracking_pro_courier_slugs: cleaned });
      setSlugs({ ...cleaned });
      setSlugsSaved({ ...cleaned });
      toast.success('Carrier mapping saved');
    } catch (err: unknown) {
      console.error('Failed to save carrier mapping', err);
      toast.error('Failed to save carrier mapping');
      setSlugs({ ...slugsSaved });
    } finally {
      setSlugsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tracking PRO</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {configured ? (
            <div className="flex items-start gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded p-3">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Provider API key detected. Courier tracking can be switched on below.
              </span>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                No provider API key found. Set <code>AFTERSHIP_API_KEY</code> (and{' '}
                <code>AFTERSHIP_WEBHOOK_SECRET</code> for live push updates) in the
                application environment. Until then the switches below have no effect.
              </span>
            </div>
          )}

          {ROW_META.map((row) => (
            <div key={row.key} className="flex items-start justify-between gap-6">
              <div className="space-y-1">
                <Label htmlFor={row.key} className="text-base">
                  {row.title}
                </Label>
                <p className="text-sm text-muted-foreground max-w-2xl">{row.description}</p>
              </div>
              <div className="flex items-center gap-2 pt-1">
                {pendingKey === row.key && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
                <Switch
                  id={row.key}
                  checked={state[row.key]}
                  onCheckedChange={(next: boolean) => toggle(row.key, next)}
                  disabled={pendingKey !== null}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Carrier Mapping</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Maps each courier on an ASN to the tracking provider&apos;s carrier code.
              Leave a field blank to use the default shown. Couriers not listed here —
              including own transport — are never sent to the provider.
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-2xl">
            {Object.keys(slugDefaults).map((courier) => (
              <div key={courier} className="space-y-1">
                <Label htmlFor={`slug-${courier}`} className="uppercase text-xs">
                  {courier}
                </Label>
                <Input
                  id={`slug-${courier}`}
                  value={slugs[courier] ?? ''}
                  placeholder={slugDefaults[courier]}
                  onChange={(e) =>
                    setSlugs((s) => ({ ...s, [courier]: e.target.value }))
                  }
                />
              </div>
            ))}
          </div>

          <Button onClick={saveSlugs} disabled={!slugsDirty || slugsSaving}>
            {slugsSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save carrier mapping
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
